import { z } from 'zod';
// ───────────────────────────────────────────────────────────────
// 스냅샷 본체 (collector → store → UI 공통)
// ───────────────────────────────────────────────────────────────
export const GpuProcessTypeSchema = z.enum(['C', 'G', 'C+G']);
export const GpuProcessSchema = z.object({
    pid: z.number().int(),
    name: z.string(),
    type: GpuProcessTypeSchema,
    // 모델명 등 보조 식별자. 없으면 '<Unknown>'.
    identifier: z.string(),
    usedMemoryBytes: z.number().nonnegative(),
});
export const GpuSnapshotSchema = z.object({
    index: z.number().int().min(0),
    name: z.string(),
    temperatureC: z.number().nullable(),
    powerW: z.number().nullable(),
    utilizationPct: z.number().min(0).max(100).nullable(),
    memoryUsedBytes: z.number().nonnegative(),
    memoryTotalBytes: z.number().positive(),
    processes: z.array(GpuProcessSchema),
});
export const CpuSnapshotSchema = z.object({
    utilizationPct: z.number().min(0).max(100).nullable(),
    temperatureC: z.number().nullable(),
    cores: z.number().int().nonnegative(),
});
export const MemorySnapshotSchema = z.object({
    usedBytes: z.number().nonnegative(),
    totalBytes: z.number().positive(),
});
export const DiskSnapshotSchema = z.object({
    mountPath: z.string(),
    usedBytes: z.number().nonnegative(),
    totalBytes: z.number().positive(),
});
export const ResourceSnapshotSchema = z.object({
    collectedAt: z.string(), // ISO timestamp
    gpus: z.array(GpuSnapshotSchema),
    cpu: CpuSnapshotSchema,
    memory: MemorySnapshotSchema,
    disk: DiskSnapshotSchema,
});
// ───────────────────────────────────────────────────────────────
// Collector wire format — `@innermate/resource-collector` 가 리턴.
// 서버는 호스트별 prevCpu 를 유지해 utilizationPct 를 산출하므로
// 콜렉터는 raw jiffies (cpuStat) 만 넘기고 CPU delta 계산은 서버측.
// ───────────────────────────────────────────────────────────────
export const CollectorCpuStatSchema = z.object({
    user: z.number(),
    nice: z.number(),
    system: z.number(),
    idle: z.number(),
    iowait: z.number(),
    irq: z.number(),
    softirq: z.number(),
    steal: z.number(),
});
export const CollectorReadResponseSchema = z.object({
    /**
     * collector 가 채워 보내지만 호스트 시계가 어긋날 수 있으므로 서버측에서 push 시점에 덮어씀.
     * 그래도 형식만은 ISO 8601 강제 — 잘못된 값이 그대로 DB 로 흘러가는 사고 차단.
     */
    collectedAt: z.string().datetime({ offset: true }),
    gpus: z.array(GpuSnapshotSchema),
    cpuStat: CollectorCpuStatSchema.nullable(),
    cpuCores: z.number().int().nonnegative(),
    cpuTempC: z.number().nullable(),
    memory: MemorySnapshotSchema,
    disk: DiskSnapshotSchema,
});
// ───────────────────────────────────────────────────────────────
// 호스트 (응답: 비밀 제외)
// ───────────────────────────────────────────────────────────────
export const ResourceHostKindSchema = z.enum(['remote', 'local']);
export const ResourceHostStatusSchema = z.enum(['ok', 'error', 'disabled', 'pending']);
export const ResourceHostSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    kind: ResourceHostKindSchema,
    /** kind='remote' 일 때만 의미 있음. 'local' 은 null. */
    collectorUrl: z.string().nullable(),
    hasAuthToken: z.boolean(),
    sampleIntervalSec: z.number().int().min(1).max(60),
    diskMountPath: z.string(),
    enabled: z.boolean(),
    status: ResourceHostStatusSchema,
    lastSeenAt: z.string().nullable(),
    lastError: z.string().nullable(),
    lastErrorAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ResourceHostListSchema = z.object({
    hosts: z.array(ResourceHostSchema),
});
// ───────────────────────────────────────────────────────────────
// 요청 (등록 / 수정 / 테스트)
// ───────────────────────────────────────────────────────────────
const NameSchema = z.string().min(1).max(80);
const SampleIntervalSchema = z.number().int().min(1).max(60);
/**
 * 디스크 마운트 경로. collector 가 shell 에 그대로 넣어 df 호출하므로 메타문자 차단:
 * - 반드시 `/` 로 시작
 * - 영문/숫자/`.`/`_`/`-`/`/` 만 허용
 *
 * Trailing slash 는 transform 으로 제거해 allowlist 매칭/저장값 일관성 유지
 * (`/data` 와 `/data/` 가 서로 다른 키로 취급되어 매칭 실패하던 함정 차단).
 * 단, 루트 `/` 는 보존.
 */
const DiskMountSchema = z
    .string()
    .min(1)
    .max(255)
    .regex(/^\/[a-zA-Z0-9/._-]*$/, '디스크 마운트 경로는 / 로 시작하고 영문/숫자/./_/-/ 만 사용할 수 있습니다.')
    .transform((v) => (v.length > 1 ? v.replace(/\/+$/, '') : v));
/**
 * resource-collector 의 base URL. 'http://10.0.0.5:7234' 형식.
 * trailing slash 는 허용하되 path 는 비워야 함 (collector 는 /health, /metrics 만 노출).
 */
const CollectorUrlSchema = z
    .string()
    .min(1)
    .max(255)
    .url('collector URL 형식이 올바르지 않습니다 (예: http://10.0.0.5:7234).')
    .refine((v) => v.startsWith('http://') || v.startsWith('https://'), {
    message: 'collector URL 은 http:// 또는 https:// 로 시작해야 합니다.',
});
const AuthTokenSchema = z.string().min(8).max(512);
export const CreateResourceHostRequestSchema = z
    .object({
    name: NameSchema,
    kind: ResourceHostKindSchema.default('remote'),
    /** kind='remote' 면 필수. kind='local' 이면 무시. */
    collectorUrl: CollectorUrlSchema.optional(),
    /** kind='remote' 면 필수. kind='local' 이면 무시. */
    authToken: AuthTokenSchema.optional(),
    sampleIntervalSec: SampleIntervalSchema.default(5),
    diskMountPath: DiskMountSchema.default('/'),
    enabled: z.boolean().default(true),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.kind === 'remote') {
        if (!val.collectorUrl) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['collectorUrl'],
                message: 'remote 호스트는 collector URL 이 필요합니다.',
            });
        }
        if (!val.authToken) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['authToken'],
                message: 'remote 호스트는 인증 토큰이 필요합니다.',
            });
        }
    }
});
export const UpdateResourceHostRequestSchema = z
    .object({
    name: NameSchema.optional(),
    kind: ResourceHostKindSchema.optional(),
    collectorUrl: CollectorUrlSchema.optional(),
    /** 비워두면 기존 유지, 값이 들어오면 교체. */
    authToken: AuthTokenSchema.optional(),
    sampleIntervalSec: SampleIntervalSchema.optional(),
    diskMountPath: DiskMountSchema.optional(),
    enabled: z.boolean().optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    // local 로 명시 전환할 때 url/token 을 같이 보내는 건 의미 없으므로 차단해 혼선 방지.
    if (val.kind === 'local') {
        if (val.collectorUrl !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['collectorUrl'],
                message: 'local 호스트는 collector URL 을 지정할 수 없습니다.',
            });
        }
        if (val.authToken !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['authToken'],
                message: 'local 호스트는 인증 토큰을 지정할 수 없습니다.',
            });
        }
    }
    // local → remote 명시 전환 시 url + token 둘 다 같은 요청에 포함돼야 함
    // (이전 값이 null 이라 핸들러에서 잡히긴 하지만 shared 단계에서 막아 일관성 ↑).
    if (val.kind === 'remote') {
        if (val.collectorUrl === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['collectorUrl'],
                message: 'remote 로 전환하려면 collector URL 을 함께 입력해야 합니다.',
            });
        }
        if (val.authToken === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['authToken'],
                message: 'remote 로 전환하려면 인증 토큰을 함께 입력해야 합니다.',
            });
        }
    }
});
// 등록 전 사전 연결 테스트 — 저장 없이 입력값만으로 collector 호출.
// local 은 in-process collect() 호출 → URL/token 불필요.
export const TestResourceHostConnectionRequestSchema = z
    .object({
    kind: ResourceHostKindSchema.default('remote'),
    collectorUrl: CollectorUrlSchema.optional(),
    authToken: AuthTokenSchema.optional(),
    diskMountPath: DiskMountSchema.default('/'),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.kind === 'remote') {
        if (!val.collectorUrl) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['collectorUrl'],
                message: 'remote 테스트는 collector URL 이 필요합니다.',
            });
        }
        if (!val.authToken) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['authToken'],
                message: 'remote 테스트는 인증 토큰이 필요합니다.',
            });
        }
    }
});
export const TestResourceHostConnectionResponseSchema = z.object({
    ok: z.boolean(),
    latencyMs: z.number().int().nonnegative(),
    summary: z
        .object({
        gpuCount: z.number().int().nonnegative(),
        cpuCores: z.number().int().nonnegative(),
        memoryTotalBytes: z.number().nonnegative(),
        diskTotalBytes: z.number().nonnegative(),
    })
        .nullable(),
    error: z.string().nullable(),
});
// ───────────────────────────────────────────────────────────────
// 메트릭 조회
// ───────────────────────────────────────────────────────────────
export const ResourceMetricsLatestQuerySchema = z.object({
    hostId: z.string().uuid(),
});
export const ResourceMetricsLatestResponseSchema = z.object({
    hostId: z.string().uuid(),
    snapshot: ResourceSnapshotSchema.nullable(),
    status: ResourceHostStatusSchema,
    lastError: z.string().nullable(),
});
export const ResourceMetricsHistoryQuerySchema = z.object({
    hostId: z.string().uuid(),
    windowSec: z.coerce.number().int().min(30).max(3600).default(300),
});
export const ResourceMetricsHistoryResponseSchema = z.object({
    hostId: z.string().uuid(),
    windowSec: z.number().int(),
    snapshots: z.array(ResourceSnapshotSchema),
});
// ───────────────────────────────────────────────────────────────
// 기간별 (Phase 3) — DB 적재 데이터 기반
// ───────────────────────────────────────────────────────────────
/** UI 가 기간을 사람 친화 식별자로 보내고, 서버가 windowSec 으로 변환. */
export const ResourceMetricsRangeWindowSchema = z.enum(['1h', '6h', '24h', '7d', '30d']);
export const RANGE_WINDOW_SECONDS = {
    '1h': 60 * 60,
    '6h': 6 * 60 * 60,
    '24h': 24 * 60 * 60,
    '7d': 7 * 24 * 60 * 60,
    '30d': 30 * 24 * 60 * 60,
};
export const ResourceMetricsRangeQuerySchema = z.object({
    hostId: z.string().uuid(),
    window: ResourceMetricsRangeWindowSchema,
});
// 단일 메트릭의 평균/최대/p95/최소 통계.
export const ResourceMetricRangeStatSchema = z.object({
    avg: z.number().nullable(),
    max: z.number().nullable(),
    p95: z.number().nullable(),
    min: z.number().nullable(),
});
export const ResourceMetricsRangeGpuStatSchema = z.object({
    index: z.number().int(),
    name: z.string(),
    utilizationPct: ResourceMetricRangeStatSchema,
    temperatureC: ResourceMetricRangeStatSchema,
    powerW: ResourceMetricRangeStatSchema,
    /** VRAM 사용률 (%) — usedBytes / totalBytes × 100. */
    memoryPct: ResourceMetricRangeStatSchema,
    memoryTotalBytes: z.number().nonnegative(),
});
export const ResourceMetricsRangeStatisticsResponseSchema = z.object({
    hostId: z.string().uuid(),
    window: ResourceMetricsRangeWindowSchema,
    bucketCount: z.number().int().nonnegative(),
    cpu: z.object({
        utilizationPct: ResourceMetricRangeStatSchema,
        temperatureC: ResourceMetricRangeStatSchema,
        cores: z.number().int().nonnegative(),
    }),
    memory: ResourceMetricRangeStatSchema, // %
    memoryTotalBytes: z.number().nonnegative(),
    disk: ResourceMetricRangeStatSchema, // %
    diskTotalBytes: z.number().nonnegative(),
    gpus: z.array(ResourceMetricsRangeGpuStatSchema),
});
// 시계열 (분 단위 + 큰 윈도우는 서버가 더 큰 step 으로 다운샘플링).
export const ResourceMetricsRangeTimeseriesPointSchema = z.object({
    bucketAt: z.string(), // ISO
    cpuUtilPct: z.number().nullable(),
    cpuTempC: z.number().nullable(),
    memoryPct: z.number().nullable(),
    diskPct: z.number().nullable(),
    /** GPU 별 사용률(%) 배열. index 순서. */
    gpuUtilPcts: z.array(z.number().nullable()),
    /** GPU 별 VRAM 사용률(%) 배열. */
    gpuMemoryPcts: z.array(z.number().nullable()),
    /** GPU 별 전력량(W) 배열. */
    gpuPowerWs: z.array(z.number().nullable()),
    /** GPU 별 온도(°C) 배열. */
    gpuTemperatureCs: z.array(z.number().nullable()),
});
export const ResourceMetricsRangeTimeseriesResponseSchema = z.object({
    hostId: z.string().uuid(),
    window: ResourceMetricsRangeWindowSchema,
    /** 인접 두 포인트 사이의 간격(초). 1m / 5m / 15m / 60m 중 하나. */
    stepSec: z.number().int().positive(),
    /** UI 에 노출할 GPU 메타 (라벨 매핑용). */
    gpus: z.array(z.object({ index: z.number().int(), name: z.string() })),
    points: z.array(ResourceMetricsRangeTimeseriesPointSchema),
});
// 사용률 분포 (히스토그램). 0~100% 를 5개 bin (0-20, 20-40, ...) 으로.
export const ResourceMetricsRangeDistributionBinSchema = z.object({
    lower: z.number(), // inclusive
    upper: z.number(), // exclusive (마지막 bin 은 inclusive)
    /** 이 구간에 속한 bucket 수 (= 분 단위 row 수). */
    bucketCount: z.number().int().nonnegative(),
    /** 전체 대비 비율 (0~1). */
    ratio: z.number().min(0).max(1),
});
export const ResourceMetricsRangeDistributionMetricSchema = z.enum([
    'cpu',
    'cpu_temp',
    'memory',
    'disk',
    'gpu_util',
    'gpu_memory',
    'gpu_power',
    'gpu_temp',
]);
export const ResourceMetricsRangeDistributionResponseSchema = z.object({
    hostId: z.string().uuid(),
    window: ResourceMetricsRangeWindowSchema,
    metric: ResourceMetricsRangeDistributionMetricSchema,
    /** gpu_util / gpu_memory 의 경우 어느 GPU 인지. CPU/Memory/Disk 는 null. */
    gpuIndex: z.number().int().nullable(),
    bins: z.array(ResourceMetricsRangeDistributionBinSchema),
    totalBucketCount: z.number().int().nonnegative(),
});
// 시간대(hour) × 요일(dow) 히트맵.
export const ResourceMetricsRangeHeatmapCellSchema = z.object({
    dayOfWeek: z.number().int().min(0).max(6), // 0 = 일요일 (UTC)
    hourOfDay: z.number().int().min(0).max(23),
    avg: z.number().nullable(),
    bucketCount: z.number().int().nonnegative(),
});
export const ResourceMetricsRangeHeatmapResponseSchema = z.object({
    hostId: z.string().uuid(),
    window: ResourceMetricsRangeWindowSchema,
    metric: ResourceMetricsRangeDistributionMetricSchema,
    gpuIndex: z.number().int().nullable(),
    cells: z.array(ResourceMetricsRangeHeatmapCellSchema),
});
// gpu_util/gpu_memory 분포 / 히트맵에 필요한 gpuIndex 쿼리 파라미터.
export const ResourceMetricsRangeMetricQuerySchema = ResourceMetricsRangeQuerySchema.extend({
    metric: ResourceMetricsRangeDistributionMetricSchema,
    gpuIndex: z.coerce.number().int().min(0).optional(),
});
//# sourceMappingURL=resource-metrics.js.map